import $ from 'jquery';

function install(editor) {
  const $editorContainer = $(editor.view.area.container);

  let selectedNodes = new Set();

  function resetSelection(toNode = null) {
    editor.selected.list = toNode ? [toNode] : [];
    selectedNodes = new Set();
  }

  function isSelectionLocked() {
    return selectedNodes.size > 0;
  }

  editor.on('selectnode', (payload) => {
    if (payload.accumulate) {
      editor.selected.list.forEach(node => {
        selectedNodes.add(node)
      })
      selectedNodes.add(payload.node);
    }
  });

  editor.on('noderemoved', (node) => {
    selectedNodes.delete(node);
  });

  // By default rete only preserves the accumulation
  // if Ctrl is held when moving nodes
  // In order to force rete to move all selected nodes
  // even when Ctrl is not held, we reselect them automatically
  // But because that triggers an infinite loop
  // we disable this callback in the current stack
  let disabled = false;
  editor.on('selectnode', (payload) => {
    if (!isSelectionLocked()) {
      return;
    }
    if (disabled) {
      return;
    }
    disabled = true;
    setTimeout(() => {
      disabled = false;
    });
    // If we select a node that is not in the current selection
    // without holding control, then we can clear the selection
    if (!selectedNodes.has(payload.node) && !payload.accumulate) {
      return;
    }
    editor.selected.list = [...selectedNodes];
    selectedNodes.forEach((node) => {
      const nodeView = editor.view.nodes.get(node);
      // Node no longer exists in the editor
      // Remove it from selected nodes
      if (!nodeView) {
        selectedNodes.delete(node);
        return;
      }
      nodeView.trigger('selectnode', { node, accumulate: true });
    });
  });

  // Reset the selection when the user clicks on a
  // node without moving it
  let activeTranslateNode = null;
  editor.on('nodecreated', (node) => {
    const nodeView = editor.view.nodes.get(node);
    const $nodeEl = $(nodeView.el);
    let wasRightClick = false;
    $nodeEl.on('mousedown', e => {
      wasRightClick = (e.buttons & 0x2) !== 0;
    });
    $nodeEl.on('mouseup', (e) => {
      if (!e.ctrlKey && !wasRightClick && !activeTranslateNode) {
        resetSelection(node);
      }
      activeTranslateNode = null;
    });
  });
  editor.on('nodetranslate', (payload) => {
    activeTranslateNode = payload.node;
  });

  // Prevent translate when lasso is enabled
  let isLassoEnabled = false;

  editor.events.lassoEnable = [];
  editor.events.lassoDisable = [];
  editor.events.nodesLassoed = [];
  function toggleLasso(toValue = !isLassoEnabled) {
    isLassoEnabled = toValue;
  }
  editor.on('lassoEnable', () => toggleLasso(true));
  editor.on('lassoDisable', () => toggleLasso(false));

  editor.on('translate', () => {
    return !isLassoEnabled;
  });

  let lassoStartCoords = null;
  let lassoEndCoords = null;
  let $lassoEl = $('<div class="lasso" style="display: none; border: dotted 2px gray; position: absolute; left: 0; top: 0; z-index: 1;"></div>');
  $editorContainer.append($lassoEl);

  $editorContainer.on('mousedown', (e) => {
    if (editor.activeTool === 'grab') {
      return;
    }
    if (e.buttons > 3) {
      return;
    }
    // Lasso select can be started from frame nodes
    const clickedOnFrameOrGroup = e.target.classList.contains('node') && (e.target.classList.contains('frame') || e.target.classList.contains('group'));
    // connection SVG rectangles are rendered within the case logic canvas
    // even though they are invisible. They shouldn't prevent lasso start
    const clickedOnConnectionSvg = e.target.classList.contains('connection');
    if (e.target !== e.currentTarget && !clickedOnFrameOrGroup && !clickedOnConnectionSvg) {
      return;
    }
    resetSelection();
    if (isLassoEnabled) {
      const x = e.pageX - $editorContainer.offset().left;
      const y = e.pageY - $editorContainer.offset().top;
      lassoStartCoords = { x, y };
    }
  });

  $editorContainer.on('mousemove', (e) => {
    if (lassoStartCoords) {
      const x = e.pageX - $editorContainer.offset().left;
      const y = e.pageY - $editorContainer.offset().top;
      lassoEndCoords = { x, y };
      redrawLasso();
    }
  });

  $editorContainer.on('mouseup', (e) => {
    if (lassoStartCoords) {
      const x = e.pageX - $editorContainer.offset().left;
      const y = e.pageY - $editorContainer.offset().top;
      lassoEndCoords = { x, y };
      processLasso();
    }
  });

  $(document).on('keydown', (e) => {
    if (e.key === 'Shift') {
      toggleLasso(true);
    }
  });
  $(document).on('keyup', (e) => {
    if (e.key === 'Shift') {
      if (!lassoStartCoords) {
        toggleLasso(false);
      } else {
        // If the shift key is released when lasso is being drawn
        // disable the lasso on mouseup
        $editorContainer.on('mouseup', () => {
          toggleLasso(false);
        })
      }
    }
  });

  function processLasso() {
    if (lassoStartCoords && lassoEndCoords) {
      const zoom = editor.view.area.transform.k;
      const lassoRect = {
        x: Math.min(lassoStartCoords.x, lassoEndCoords.x),
        y: Math.min(lassoStartCoords.y, lassoEndCoords.y),
        w: Math.abs(lassoEndCoords.x - lassoStartCoords.x),
        h: Math.abs(lassoEndCoords.y - lassoStartCoords.y),
      };
      editor.nodes.forEach((node) => {
        const nodeView = editor.view.nodes.get(node);
        const $nodeEl = $(nodeView.el);
        const nodeRect = {
          x: $nodeEl.offset().left - $editorContainer.offset().left,
          y: $nodeEl.offset().top - $editorContainer.offset().top,
          w: $nodeEl.width() * zoom,
          h: $nodeEl.height() * zoom,
        };
        let select = false;
        if (node.name === 'Frame' || node.name === 'Group') {
          select = rectContainsOrIntersectsBorders(lassoRect, nodeRect);
        } else {
          select = rectsOverlap(lassoRect, nodeRect);
        }
        if (select) {
          nodeView.trigger('selectnode', { node, accumulate: true });
          selectedNodes.add(node);
        }
      });
      editor.trigger('nodesLassoed');
    }
    eraseLasso();
  }

  function redrawLasso() {
    const lassoRect = {
      x: Math.min(lassoStartCoords.x, lassoEndCoords.x),
      y: Math.min(lassoStartCoords.y, lassoEndCoords.y),
      w: Math.abs(lassoEndCoords.x - lassoStartCoords.x),
      h: Math.abs(lassoEndCoords.y - lassoStartCoords.y),
    };
    $lassoEl.css({
      left: lassoRect.x,
      top: lassoRect.y,
      width: lassoRect.w,
      height: lassoRect.h
    })
    $lassoEl.show();
  }

  function eraseLasso() {
    lassoStartCoords = null;
    lassoEndCoords = null;
    $lassoEl.hide();
  }
}

function rectsOverlap(rect1, rect2) {
  const xOverlap = (rect1.x >= rect2.x && rect1.x <= (rect2.x + rect2.w)) || (rect2.x >= rect1.x && rect2.x <= (rect1.x + rect1.w));
  const yOverlap = (rect1.y >= rect2.y && rect1.y <= (rect2.y + rect2.h)) || (rect2.y >= rect1.y && rect2.y <= (rect1.y + rect1.h));
  return xOverlap && yOverlap;
}

function rectContainsOrIntersectsBorders(rect1, rect2) {
	return rectsOverlap(rect1, rect2) && !isRectInRect(rect2, rect1);
}

function isRectInRect(rect1, rect2) {
  if (
    rect2.x + rect2.w < rect1.x + rect1.w &&
    rect2.x > rect1.x &&
    rect2.y > rect1.y &&
    rect2.y + rect2.h < rect1.y + rect1.h
  ) {
    return true;
  } else {
    return false;
  }
}

export default {
  name: 'lasso-select',
  install,
};
